import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle } from 'lucide-react';
import { authAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

export const ForgotPasswordDialog = ({ open, onOpenChange, defaultEmail }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) { setEmail(defaultEmail || ''); setSent(false); }
  }, [open, defaultEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword({ email });
      setSent(true);
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>Forgot Password</DialogTitle>
          <DialogDescription>Enter your registered email. A new password will be sent to it.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="flex items-start gap-3 rounded-lg border bg-green-50 dark:bg-green-950/30 p-4 text-sm" data-testid="forgot-password-success">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            <p>If <strong>{email}</strong> is registered, a new password has been sent to it. Please check your inbox (and spam folder).</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} id="forgot-form" className="space-y-2">
            <Label htmlFor="forgot-email">Email</Label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input id="forgot-email" type="email" required className="pl-9" placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
            </div>
          </form>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="forgot-close-btn">{sent ? 'Back to Login' : 'Cancel'}</Button>
          {!sent && (
            <Button type="submit" form="forgot-form" disabled={loading} data-testid="forgot-submit-btn">
              {loading ? 'Sending...' : 'Send New Password'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
